import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Search, User, ChevronDown, LayoutGrid, List, Archive, Filter, Settings, Workflow, Menu, X, MessageSquare, FileText, Radio } from 'lucide-react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import UserProfileModal from './UserProfileModal';
import { handoffApi } from '../../services/api';
import Toast from '../ui/Toast';

// Memoized ViewSwitcherDropdown component
const ViewSwitcherDropdown = React.memo(({ onCreateClick, isMobile = false }) => {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();

  const views = useMemo(() => {
    const role = user?.role;
    if (!role) return [];

    const allViews = [
      {
        id: 'board',
        name: 'Kanban Board',
        description: 'Visual board with drag & drop',
        icon: LayoutGrid,
        path: '/softeamboard',
        active: location.pathname === '/softeamboard'
      },
      {
        id: 'list',
        name: 'Needs List',
        description: 'Detailed table view with filters',
        icon: List,
        path: '/softeamboard/needs',
        active: location.pathname === '/softeamboard/needs'
      },
      {
        id: 'candidates',
        name: 'Candidates',
        description: 'Search and manage all candidates',
        icon: User,
        path: '/candidates',
        active: location.pathname === '/candidates'
      },
      {
        id: 'finished',
        name: 'Finished Needs',
        description: 'Completed, sold, or canceled needs',
        icon: Archive,
        path: '/softeamboard/finished',
        active: location.pathname === '/softeamboard/finished'
      },
      {
        id: 'jobconnect-publications',
        name: 'Diffusions JobConnect',
        description: 'Suivre les offres diffusées et leurs candidatures',
        icon: Radio,
        path: '/jobconnect-publications',
        active: location.pathname === '/jobconnect-publications'
      },
      {
        id: 'workflow-requests',
        name: 'Workflow Requests',
        description: 'Manage workflow requests and triggers',
        icon: Workflow,
        path: '/admin/workflows',
        active: location.pathname.startsWith('/admin/workflows')
      },
      {
        id: 'meeting-minutes',
        name: 'Comptes-rendus',
        description: 'Generate meeting minutes from audio',
        icon: FileText,
        path: '/meeting-minutes',
        active: location.pathname === '/meeting-minutes'
      },
      {
        id: 'flows-admin',
        name: 'Flow Administration',
        description: 'Monitor and control Kafka flows',
        icon: Settings,
        path: '/admin/flows',
        active: location.pathname === '/admin/flows',
        adminOnly: true
      }
    ];

    if (role === 'Admin') return allViews;
    if (role === 'Recruiter') return allViews.filter(v => ['board', 'meeting-minutes'].includes(v.id));
    // Sales: everything except admin-only
    return allViews.filter(v => !v.adminOnly);
  }, [location.pathname, user?.role]);

  const currentView = useMemo(() => {
    if (views.length === 0) return null;
    return views.find(view => view.active) || views[0];
  }, [views]);

  const handleViewChange = useCallback((view) => {
    if (view.action === 'create') {
      onCreateClick?.();
    } else {
      navigate(view.path);
    }
    setIsOpen(false);
  }, [navigate, onCreateClick]);

  const handleToggle = useCallback(() => {
    setIsOpen(prev => !prev);
  }, []);

  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event) => {
      if (!event.target.closest('.view-switcher-dropdown')) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  // Mobile dropdown positioning
  const dropdownClasses = isMobile 
    ? "absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-50 animate-in slide-in-from-top-1 duration-200"
    : "absolute top-full left-0 mt-1 w-64 bg-white border border-gray-200 rounded-lg shadow-lg z-50 animate-in slide-in-from-top-1 duration-200";

  if (!currentView) return null;

  return (
    <div className="relative view-switcher-dropdown">
      <button
        onClick={handleToggle}
        className={`flex items-center space-x-2 px-3 py-2 text-sm text-gray-700 hover:text-gray-900 hover:bg-gray-100 rounded-md transition-colors border border-gray-300 ${
          isMobile ? 'w-full justify-between' : ''
        }`}
      >
        <div className="flex items-center space-x-2">
          <currentView.icon size={16} />
          <span className="font-medium hidden sm:inline">{currentView.name}</span>
          <span className="font-medium sm:hidden">{currentView.name.length > 12 ? currentView.name.substring(0, 12) + '...' : currentView.name}</span>
        </div>
        <ChevronDown 
          size={16} 
          className={`transform transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} 
        />
      </button>

      {isOpen && (
        <div className={dropdownClasses}>
          <div className="py-1">
            {views.map((view, index) => {
              const Icon = view.icon;
              const isAdminSection = view.adminOnly && index > 0 && !views[index - 1]?.adminOnly;
              
              return (
                <React.Fragment key={view.id}>
                  {/* Add separator before admin section */}
                  {isAdminSection && (
                    <div className="border-t border-gray-200 my-1">
                      <div className="px-4 py-2">
                        <div className="text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Administration
                        </div>
                      </div>
                    </div>
                  )}
                  
                  <button
                    onClick={() => handleViewChange(view)}
                    className={`w-full flex items-start space-x-3 px-4 py-3 text-left hover:bg-gray-50 transition-colors ${
                      view.active ? 'bg-blue-50 border-r-2 border-blue-500' : ''
                    } ${view.adminOnly ? 'bg-orange-50/30' : ''}`}
                  >
                    <Icon 
                      size={18} 
                      className={`mt-0.5 flex-shrink-0 ${
                        view.active 
                          ? 'text-blue-600' 
                          : view.adminOnly 
                            ? 'text-orange-500' 
                            : 'text-gray-400'
                      }`} 
                    />
                    <div className="flex-1 min-w-0">
                      <div className={`font-medium text-sm ${
                        view.active ? 'text-blue-900' : 'text-gray-900'
                      }`}>
                        {view.name}
                        {view.adminOnly && (
                          <span className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                            Admin
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-gray-500 mt-0.5 hidden sm:block">
                        {view.description}
                      </div>
                    </div>
                    {view.active && (
                      <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0"></div>
                    )}
                  </button>
                </React.Fragment>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
});

ViewSwitcherDropdown.displayName = 'ViewSwitcherDropdown';

const AppHeader = ({ 
  searchValue = '', 
  onSearchChange = () => {}, 
  showFilters = false,
  onToggleFilters = () => {},
  showFilterButton = false,
  showSearch = true,
  searchPlaceholder = 'Search needs, clients... (⌘K)',
  searchAriaLabel = 'Search needs and clients',
  actions = null 
}) => {
  const { user } = useAuth();
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [showMobileSearch, setShowMobileSearch] = useState(false);
  const [toast, setToast] = useState(null);

  // Memoize callbacks to prevent unnecessary re-renders
  const handleSearchChange = useCallback((e) => {
    onSearchChange(e.target.value);
  }, [onSearchChange]);

  const handleUserAvatarClick = useCallback(() => {
    setShowProfileModal(true);
  }, []);

  const handleCloseProfileModal = useCallback(() => {
    setShowProfileModal(false);
  }, []);

  const handleMobileMenuToggle = useCallback(() => {
    setShowMobileMenu(prev => !prev);
  }, []);

  const handleMobileSearchToggle = useCallback(() => {
    setShowMobileSearch(prev => !prev);
  }, []);

  // Handle chatbot navigation with handoff
  const handleChatbotNavigation = useCallback(async () => {
    const CHATBOT_URL = 'https://chatbot.paca.softeam.expert/';
    
    try {
      // Create handoff code with chatbot URL as destination
      const handoffResponse = await handoffApi.createHandoffCode({
        destination_url: CHATBOT_URL,
        audience: CHATBOT_URL
      });
      
      // Navigate to chatbot with handoff code as query parameter
      const url = new URL(CHATBOT_URL);
      url.searchParams.set('handoff_code', handoffResponse.code);
      window.location.href = url.toString();
    } catch (error) {
      console.error('Failed to create handoff code for chatbot:', error);
      // Show error message to user
      setToast({ message: 'Unable to authenticate with chatbot. Please try again later.', type: 'error' });
    }
  }, []);

  // Handle keyboard shortcut if search is shown
  useEffect(() => {
    if (!showSearch) return;
    
    const handleKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        const searchInput = document.getElementById('global-search');
        if (searchInput) {
          searchInput.focus();
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [showSearch]);

  // Close mobile menu when clicking outside
  useEffect(() => {
    if (!showMobileMenu) return;

    const handleClickOutside = (event) => {
      if (!event.target.closest('.mobile-menu')) {
        setShowMobileMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showMobileMenu]);

  return (
    <>
      <header className="bg-white border-b border-gray-200 px-2 sm:px-4 py-2 sm:py-3 sticky top-0 z-40">
        {/* Desktop Layout */}
        <div className="hidden md:flex items-center justify-between">
          <nav aria-label="Main navigation" className="flex items-center space-x-4">
            {/* Filter button (conditionally shown) */}
            {showFilterButton && (
              <button
                onClick={onToggleFilters}
                className="flex items-center px-3 py-2 text-sm text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-md transition-colors border border-gray-300"
              >
                <Filter size={16} className="mr-2" />
                Filters
              </button>
            )}

            {/* Logo */}
            <Link to="/softeamboard" className="flex items-center hover:opacity-80 transition-opacity">
              <img
                src="/logosofteamcat.png"
                alt="Softeam IA"
                className="h-16 sm:h-20 w-auto"
              />
            </Link>

            {/* View Switcher Dropdown */}
            {user?.role !== 'TranscriptOnly' && (
              <ViewSwitcherDropdown />
            )}
          </nav>

          {/* Global search - only show if showSearch is true */}
          {showSearch && (
            <div className="flex-1 max-w-md mx-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none" size={16} />
                <input
                  id="global-search"
                  type="text"
                  placeholder={searchPlaceholder}
                  value={searchValue}
                  onChange={handleSearchChange}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                  aria-label={searchAriaLabel}
                />
              </div>
            </div>
          )}

          {/* Right side actions */}
          <div className="flex items-center space-x-2">
            {/* Custom actions */}
            {actions}
            
            {/* Chatbot button */}
            {user && user.role !== 'TranscriptOnly' && (
              <button
                onClick={handleChatbotNavigation}
                className="flex items-center px-3 py-2 text-sm text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-md transition-colors border border-gray-300"
                title="Open Chatbot"
              >
                <MessageSquare size={16} className="mr-2" />
                <span className="hidden sm:inline">Chatbot</span>
              </button>
            )}
            
            {/* User avatar */}
            <button
              onClick={handleUserAvatarClick}
              className="w-8 h-8 bg-gray-300 rounded-full flex items-center justify-center hover:bg-gray-400 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
              title={user?.name || 'User Profile'}
              aria-label={user?.name || 'User Profile'}
            >
              {user?.avatarUrl ? (
                <img
                  src={user.avatarUrl}
                  alt={user.name}
                  className="w-8 h-8 rounded-full object-cover"
                />
              ) : (
                <User size={16} className="text-gray-600" />
              )}
            </button>
          </div>
        </div>

        {/* Mobile Layout */}
        <div className="md:hidden">
          <div className="flex items-center justify-between">
            {/* Mobile Menu Button */}
            <button
              onClick={handleMobileMenuToggle}
              className="p-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-md transition-colors"
              aria-label={showMobileMenu ? 'Close menu' : 'Open menu'}
            >
              {showMobileMenu ? <X size={20} /> : <Menu size={20} />}
            </button>

            {/* Logo - Centered on mobile */}
            <Link to="/softeamboard" className="flex items-center hover:opacity-80 transition-opacity">
              <img 
                src="/logosofteamcat.png" 
                alt="Softeam IA" 
                className="h-12 w-auto"
              />
            </Link>

            {/* Mobile Search Button */}
            {showSearch && (
              <button
                onClick={handleMobileSearchToggle}
                className="p-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-md transition-colors"
                aria-label="Toggle search"
              >
                <Search size={20} />
              </button>
            )}

            {/* User avatar */}
            <button
              onClick={handleUserAvatarClick}
              className="w-8 h-8 bg-gray-300 rounded-full flex items-center justify-center hover:bg-gray-400 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
              title={user?.name || 'User Profile'}
              aria-label={user?.name || 'User Profile'}
            >
              {user?.avatarUrl ? (
                <img
                  src={user.avatarUrl}
                  alt={user.name}
                  className="w-8 h-8 rounded-full object-cover"
                />
              ) : (
                <User size={16} className="text-gray-600" />
              )}
            </button>
          </div>

          {/* Mobile Search Bar */}
          {showSearch && showMobileSearch && (
            <div className="mt-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none" size={16} />
                <input
                  id="global-search-mobile"
                  type="text"
                  placeholder={searchPlaceholder}
                  value={searchValue}
                  onChange={handleSearchChange}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                  aria-label={searchAriaLabel}
                />
              </div>
            </div>
          )}

          {/* Mobile Menu */}
          {showMobileMenu && (
            <div className="mobile-menu mt-3 bg-white border border-gray-200 rounded-lg shadow-lg">
              <div className="p-4 space-y-4">
                {/* Filter button */}
                {showFilterButton && (
                  <button
                    onClick={onToggleFilters}
                    className="w-full flex items-center px-3 py-2 text-sm text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-md transition-colors border border-gray-300"
                  >
                    <Filter size={16} className="mr-2" />
                    Filters
                  </button>
                )}

                {/* View Switcher for Mobile */}
                {user?.role !== 'TranscriptOnly' && (
                  <div className="space-y-2">
                    <div className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">
                      Navigation
                    </div>
                    <ViewSwitcherDropdown isMobile={true} />
                  </div>
                )}

                {/* Custom actions */}
                {actions && (
                  <div className="space-y-2">
                    <div className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">
                      Actions
                    </div>
                    <div className="space-y-2">
                      {actions}
                    </div>
                  </div>
                )}

                {/* Chatbot button for mobile */}
                {user && user.role !== 'TranscriptOnly' && (
                  <button
                    onClick={handleChatbotNavigation}
                    className="w-full flex items-center px-3 py-2 text-sm text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-md transition-colors border border-gray-300"
                  >
                    <MessageSquare size={16} className="mr-2" />
                    Chatbot
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      </header>

      {/* User Profile Modal */}
      {showProfileModal && (
        <UserProfileModal
          isOpen={showProfileModal}
          onClose={handleCloseProfileModal}
        />
      )}

      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </>
  );
};

export default React.memo(AppHeader);
